// Settings workspace: the record-level settings — General, Approvals, Work
// rules, Publish on website, Offline & sync, Assistant authoring, Search by
// meaning, Document attachments, Additional attributes — each full width instead of squeezed into a
// 300px panel. The editors are the same ones the old tree nodes opened.

import type { TransactionModel, TreeNode } from './types'
import PropertyPanel from './PropertyPanel'
import { settingsSections, type SettingsKey } from './setupCatalog'

interface Props {
  model: TransactionModel
  section: SettingsKey
  onSection: (key: SettingsKey) => void
  onModelChange: (m: TransactionModel) => void
  onOpenCode?: (method?: string) => void
  project: string
  onGuardedNavigate?: (action: () => void) => void
  /** Jump back to Design with a form selected (the per-form settings note). */
  onSelectDesign: (node: TreeNode) => void
  compact?: boolean
}

export default function SettingsWorkspace({
  model, section, onSection, onModelChange, onOpenCode, project, onGuardedNavigate, onSelectDesign, compact,
}: Props) {
  const sections = settingsSections(model)
  const cur = sections.find(s => s.key === section) || sections[0]

  const row = (sel: boolean): React.CSSProperties => ({
    font: 'inherit', display: 'flex', alignItems: 'center', gap: 10, border: 'none', borderRadius: 6,
    padding: compact ? '11px 10px' : '9px 10px', cursor: 'pointer', textAlign: 'left', width: '100%', color: 'var(--color-text)',
    background: sel ? 'var(--color-accent-weak)' : 'transparent',
    outline: sel ? '1px solid var(--color-accent)' : 'none', outlineOffset: -1,
  })
  const pill = (on: boolean): React.CSSProperties => ({
    fontSize: '0.8125rem', fontWeight: 600, borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap',
    color: on ? 'var(--color-success, #0e6027)' : 'var(--color-text-secondary)',
    background: on ? 'var(--color-success-weak, rgba(36,161,72,.14))' : 'var(--color-surface-2)',
  })

  return (
    <div style={{ display: 'flex', flexWrap: compact ? 'wrap' : 'nowrap', flex: 1, minHeight: 0, minWidth: 0, overflow: compact ? 'auto' : 'hidden' }}>
      <aside aria-label="Settings sections" style={{
        flex: compact ? '1 1 100%' : '0 0 300px', background: 'var(--color-bg-secondary)',
        borderRight: compact ? 'none' : '1px solid var(--color-border)', borderBottom: compact ? '1px solid var(--color-border)' : 'none',
        padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: 4, overflow: 'auto',
      }}>
        <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text-secondary)', padding: '0 8px 6px' }}>This object</div>
        {compact ? (
          <select value={cur.key} onChange={e => onSection(e.target.value as SettingsKey)} style={{ padding: 10 }}>
            {sections.map(s => <option key={s.key} value={s.key}>{s.label}{s.state ? ` — ${s.state}` : ''}</option>)}
          </select>
        ) : sections.map(s => (
          <button key={s.key} type="button" style={row(s.key === cur.key)} onClick={() => onSection(s.key)}>
            <span style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: '0.9375rem', fontWeight: 500 }}>{s.label}</span>
              <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>{s.detail}</span>
            </span>
            {s.state && <span style={pill(s.on)}>{s.state}</span>}
          </button>
        ))}
        {!compact && (
          <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', padding: '12px 8px 0', lineHeight: 1.5 }}>
            Per-form settings — data source, row-level security, list view and filter sets — are on each{' '}
            <button type="button" onClick={() => onSelectDesign({ type: 'form', formIndex: 0, label: '' })} style={{
              font: 'inherit', border: 'none', background: 'none', padding: 0, color: 'var(--color-accent)', cursor: 'pointer', textDecoration: 'underline',
            }}>form&apos;s inspector in Design</button>.
          </div>
        )}
      </aside>
      <main style={{ flex: '1 1 560px', minWidth: 0, overflow: 'auto', background: 'var(--color-bg)', padding: compact ? '16px 8px 60px' : '24px 28px 60px' }}>
        {/* Additional attributes is a whole setup screen (tree, work area,
            properties): it gets the full width. */}
        <div style={{ maxWidth: cur.key === 'additional-attributes' ? 'none' : 900 }}>
          <h1 style={{ margin: '0 0 12px', fontSize: '1.3125rem', fontWeight: 600 }}>{cur.label}</h1>
          <div style={{
            background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: 8,
            padding: '8px 6px', minHeight: 200,
          }}>
            <PropertyPanel
              key={cur.key}
              model={model}
              selectedNode={cur.node}
              onModelChange={onModelChange}
              onOpenCode={onOpenCode}
              project={project}
              onGuardedNavigate={onGuardedNavigate}
              onSelectNode={(n) => {
                const target = sections.find(s => s.node.type === n.type)
                if (target) onSection(target.key)
                else onSelectDesign(n)
              }}
            />
          </div>
        </div>
      </main>
    </div>
  )
}
